import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PATH_KEYS } from "@/services/appServices";
import { useItemService } from "./useServices";
import Conversation from "@/models/conversation/Conversation";
import Message from "@/models/conversation/Message";
import { Pagination, PaginationQuery } from "@/models/base/Pagination";
import { restError } from "@/models/client/RestResponse";

const useConversation = () => {
  const queryClient = useQueryClient();

  /**** Services ****/
  const conversationService = useItemService(
    (services) => services.conversation
  );
  const messageService = useItemService((services) => services.message);

  /**** Value ****/
  const useQuerySingleConversation = (id: string | null | undefined) =>
    useQuery<Conversation | undefined, unknown, Conversation | undefined>({
      queryKey: [...PATH_KEYS.conversation, id],
      queryFn: async () => {
        if (!id) throw new Error("A conversation id is required");
        const response = await conversationService.querySingle(id);
        if (response.success) return response.value;
        else throw restError(response);
      },
      enabled: Boolean(id),
    });
  const useQueryMessagesByConversationId = (id: string | null | undefined) =>
    useQuery<Message[], unknown, Message[]>({
      queryKey: [...PATH_KEYS.message, id],
      queryFn: async () => {
        if (!id) throw new Error("A conversation id is required");
        const response = await messageService.queryByConversationId({
          conversationId: id,
        });
        if (response.success) return response.value;
        else throw restError(response);
      },
      enabled: Boolean(id),
    });

  const useQueryAllConversations = (query: PaginationQuery) =>
    useQuery<Pagination<Conversation>>({
      queryKey: [...PATH_KEYS.conversation, "pagination", query],
      queryFn: async () => {
        const response = await conversationService.getAllConversations({
          pageNumber: query.pageNumber,
          pageSize: query.pageSize,
          sortAscending: query.sortAscending,
          sortBy: query.sortBy,
          search: query.search,
          filters: query.filters,
        });
        if (response.success) return response.value;
        else throw restError(response);
      },
    });

  // Admin delete of any user's conversation (LAR-27 #5). Requires the
  // DeleteAnyConversation permission server-side.
  const deleteConversationAsAdmin = useMutation<void, Error, string>({
    mutationKey: [...PATH_KEYS.conversation, "delete-admin"],
    mutationFn: async (conversationId: string) => {
      const response = await conversationService.deleteAsAdmin(conversationId);
      if (!response.success) throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...PATH_KEYS.conversation],
      });
    },
  });

  return {
    useQuerySingleConversation,
    useQueryMessagesByConversationId,
    useQueryAllConversations,
    deleteConversationAsAdmin,
  };
};

export default useConversation;
